<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Section, SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { Branding } from '@client/pages/manage/domain-settings/branding';
  import { Compliance } from '@client/pages/manage/domain-settings/compliance';
  import { CRM } from '@client/pages/manage/domain-settings/crm';
  import { Deliverabilty } from '@client/pages/manage/domain-settings/deliverability';
  import { Directory } from '@client/pages/manage/domain-settings/directory';
  import { DOMAIN_CATEGORIES } from '@client/pages/manage/domain-settings/domain-config';
  import { General } from '@client/pages/manage/domain-settings/general';
  import { Google } from '@client/pages/manage/domain-settings/google';
  import { Integrations } from '@client/pages/manage/domain-settings/integrations';
  import { Marketing } from '@client/pages/manage/domain-settings/marketing';
  import { Outlook } from '@client/pages/manage/domain-settings/outlook';
  import { Routing } from '@client/pages/manage/domain-settings/routing';
  import { Signature } from '@client/pages/manage/domain-settings/signature';
  import { SSO } from '@client/pages/manage/domain-settings/sso';

  import { DomainService } from '@client/services/domain';

  import { hasCapability, userStore } from '@client/stores/user';

  import { deepCopyArray } from '@common/utils/arrays';
  import { deepCopy, merge } from '@common/utils/objects';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let settings = null;
  let initialSettings = null;
  let categories = [];
  let selectedCategory = null;
  let updatedSettings = {};
  let updatedSettingsList = [];
  let originalValues = {};

  // Component mapping for each section
  const sectionComponents = {
    general: General,
    branding: Branding,
    routing: Routing,
    deliverabilty: Deliverabilty,
    sso: SSO,
    crm: CRM,
    marketing: Marketing,
    compliance: Compliance,
    outlookaddin: Outlook,
    gmailaddin: Google,
    signature: Signature,
    directory: Directory,
    integrations: Integrations
  };

  // Search words mapping for each category
  const categorySearchWords = {
    general: [
      'white',
      'label',
      'domain',
      'alias',
      'aliases',
      'internal',
      'domains',
      'cname',
      'ssl',
      'tls',
      'hsts',
      'logo',
      'favicon',
      'icon',
      'redirect',
      'cdn',
      'inline',
      'images',
      'dynamic',
      'groups',
      'pgp',
      'encryption',
      'clone',
      'settings',
      'suspend',
      'service',
      'signature',
      'block',
      'code',
      'stamping',
      'quoted',
      'full',
      'reply',
      'tag',
      'replacement',
      'placement',
      'banner',
      'banners',
      'default',
      'disable',
      'business',
      'card',
      'cards',
      'private',
      'vcard',
      'vcards',
      'field',
      'mappings',
      'billing',
      'warning',
      'attachment',
      'view',
      'download',
      'template',
      'advanced',
      'templating',
      'engine',
      'from',
      'overrides',
      'body',
      'header',
      'slide',
      'time',
      'auto',
      'track',
      'tracking',
      'document',
      'playback',
      'internal',
      'multi',
      'recipient',
      'processing',
      'non',
      'sales',
      'users',
      'compliance',
      'metadata',
      'surveys',
      'clear',
      'duplicate',
      'disclaimers',
      'smime',
      'gmail',
      'ellipsing',
      'website',
      'site',
      'ips',
      'feeds',
      'contact',
      'enrichment',
      'buyer',
      'roles',
      'departments',
      'executive',
      'keywords',
      'influencer',
      'excluded',
      'personas',
      'fields',
      'settings',
      'products',
      'email',
      'emails',
      'links',
      'attachments',
      'dwell',
      'icon',
      'original',
      'top',
      'bcc',
      'not',
      'list',
      'smart',
      'activity',
      'feed',
      'forwarded',
      'shortcut',
      'notification',
      'third',
      'party',
      'push',
      'post',
      'endpoint',
      'authorization',
      'token',
      'audit',
      'history'
    ],
    branding: [
      'branding',
      'company',
      'name',
      'url',
      'logo',
      'icon',
      'theme',
      'primary',
      'color',
      'secondary',
      'header',
      'font',
      'weight',
      'size',
      'background'
    ],
    routing: [
      'routing',
      'loopback',
      'only',
      'microsoft',
      '365',
      'office',
      'gcc',
      'high',
      'hostname',
      'override',
      'google',
      'workspace',
      'pool',
      'smart',
      'host',
      'hosting',
      'port',
      'user',
      'username',
      'password',
      'bulk',
      'outreach',
      'zendesk',
      'header',
      'trigger',
      'attribute',
      'relay',
      'transport'
    ],
    deliverabilty: [
      'deliverability',
      'arc',
      'seals',
      'dmarc',
      'admin',
      'email',
      'dkim',
      'enabled',
      'selector',
      'dns',
      'entry',
      'scl',
      'auth',
      'code',
      'spf',
      'cname',
      'whitelabel'
    ],
    sso: [
      'sso',
      'authentication',
      'auth',
      'policy',
      'password',
      'mfa',
      'two',
      'factor',
      'twofactor',
      'role',
      'bindings',
      'binding',
      'saml',
      'idp',
      'metadata',
      'certificate',
      'issuer',
      'entity',
      'login',
      'logout'
    ],
    crm: ['crm', 'salesforce', 'surefire', 'total', 'expert', 'totalexpert', 'integration', 'api', 'key', 'token'],
    marketing: ['marketing', 'campaigns', 'automation', 'analytics', 'reports'],
    compliance: ['compliance', 'active', 'tracking', 'notify', 'report', 'email', 'non'],
    outlookaddin: [
      'outlook',
      'addin',
      'add-in',
      'addon',
      'plugin',
      'microsoft',
      'fields',
      'user',
      'signature',
      'email',
      'tracking',
      'track',
      'alias',
      'aliases',
      'shared',
      'mailbox',
      'inboxes'
    ],
    gmailaddin: ['gmail', 'google', 'addin', 'add-in', 'addon', 'plugin', 'workspace', 'fields', 'user', 'signature'],
    signature: [
      'signature',
      'my',
      'mysig',
      'block',
      'copy',
      'banner',
      'link',
      'help',
      'text',
      'description',
      'faq',
      'custom',
      'instructions'
    ],
    directory: [
      'directory',
      'sync',
      'synchronization',
      'microsoft',
      '365',
      'office',
      'azure',
      'google',
      'workspace',
      'gsuite',
      'custom',
      'sftp',
      'ssh',
      'csv',
      'file',
      'upload',
      'configurations',
      'config',
      'tree',
      'endpoint',
      'token',
      'api'
    ],
    integrations: [
      'integrations',
      'calendly',
      'drift',
      'eloqua',
      'hubspot',
      'marketo',
      'outreach',
      'pardot',
      'rollworks',
      'salesloft',
      'zapier',
      'api',
      'key',
      'token',
      'webhook'
    ],
    diagnostics: ['diagnostics', 'debug', 'logs', 'errors', 'testing', 'troubleshoot']
  };

  // Build select options from categories
  const buildSelectOptions = (categories) => {
    return categories.map((category) => ({
      label: $format(category.label),
      value: category.id,
      categoryId: category.id,
      searchWords: categorySearchWords[category.id] || []
    }));
  };

  // Filter function for the select dropdown
  const categoryItemFilter = (label, filterText, option) => {
    const searchText = filterText.toLowerCase().trim();

    if (!searchText) {
      return true;
    }

    // Split search text into individual words
    const searchTerms = searchText.split(/\s+/);

    // Search in label - check if all search terms are present
    const labelLower = label.toLowerCase();
    if (searchTerms.every((term) => labelLower.includes(term))) {
      return true;
    }

    // Search in searchWords array - check if all search terms match at least one searchWord
    const searchWords = option?.searchWords || [];
    const searchWordsLower = searchWords.map((word) => word.toLowerCase());

    if (searchTerms.every((term) => searchWordsLower.some((word) => word.includes(term)))) {
      return true;
    }

    return false;
  };

  // Helper to find subsection based on setting id and category
  const findSubsection = (settingId, category) => {
    if (!category || !category.items) {
      return '';
    }

    const lowerSettingId = settingId.toLowerCase();

    // Comprehensive keyword mapping to subsections
    const subsectionKeywords = {
      // General subsections
      domains: ['domainname', 'domain', 'cname', 'alias', 'internal', 'internaldomains'],
      signatures: ['scratchpad', 'signature', 'signatureconfig', 'signaturemode'],
      banners: ['banner', 'bannerdef'],
      bizcards: ['bizcard', 'bizcardconfig', 'vcard'],
      billing: ['billing', 'chargebee', 'subscription'],
      email: [
        'compliance',
        'compliancetracking',
        'defaultnotification',
        'notification',
        'emailerroralert',
        'tracking',
        'click',
        'open'
      ],
      personas: ['persona', 'role', 'department', 'executive', 'influencer'],
      users: ['defaultusersetting', 'userdefault', 'newuser'],
      'audit-history': ['audit', 'auditconfig', 'audithistory'],
      // Routing subsections
      general: ['enablerouting', 'routing'],
      smart: ['smarthost', 'smarthostrel'],
      bulk: ['bulkhost', 'bulkhostrel'],
      relay: ['transport', 'transportrule'],
      // SSO subsections
      authentication: ['authpolicy', 'auth', 'password', 'mfa', 'twofactor'],
      bindings: ['rolebinding', 'binding'],
      saml: ['saml', 'sso', 'idp', 'metadata'],
      // Outlook subsections
      fields: ['field', 'userfield', 'signaturefield'],
      'email-tracking': ['emailtrack', 'track'],
      shared: ['alias', 'shared', 'sharedmailbox'],
      // Directory subsections
      microsoft: ['office365', 'microsoft', 'azure', 'o365'],
      google: ['google', 'gsuite', 'workspace'],
      custom: ['customdir', 'custom'],
      sftp: ['sftp', 'ssh'],
      configurations: ['csv', 'csvtree', 'config']
    };

    // Check each subsection's keywords
    for (const [subsectionKey, keywords] of Object.entries(subsectionKeywords)) {
      for (const keyword of keywords) {
        if (lowerSettingId.includes(keyword)) {
          // Find the matching item in the category
          const item = category.items.find((i) => i.section === subsectionKey);
          if (item) {
            return $format(item.label);
          }
        }
      }
    }

    return '';
  };

  // Modified onUpdate function that tracks changes instead of immediately updating
  const onUpdate = async (id, label, value) => {
    const formattedId = id.replace(/-/g, '.');
    let [parent, property] = formattedId.split('.');
    let newValue = value;
    let update = {};

    // Capture original value BEFORE any mutations to initialSettings
    const captureOriginal = (key) => {
      if (key && !(key in originalValues)) {
        originalValues[key] = deepCopy(initialSettings[key]);
      }
    };

    if (
      id === 'attTemplateType' ||
      id === 'defaultsyncoptions-replace' ||
      id === 'customdirtokenmethod' ||
      id === 'customdirendpointmethod' ||
      id === 'customdirendpointpostcontenttype' ||
      id === 'customdirtokenpostcontenttype'
    ) {
      newValue = value.value;
    }

    if (id === 'authPolicy' || (parent === 'authPolicy' && property !== null)) {
      captureOriginal('authPolicy');

      if (id === 'authPolicy') {
        newValue = {
          ...initialSettings.authPolicy,
          ...value.reduce((acc, { name, value }) => ({ ...acc, [name]: Number(value) }), {})
        };
      } else {
        newValue = {
          ...initialSettings.authPolicy,
          [property]: newValue
        };
      }

      initialSettings.authPolicy = newValue;
    }

    if (id === 'auditconfig' || (parent === 'auditconfig' && property !== null)) {
      captureOriginal('auditconfig');

      if (id === 'auditconfig') {
        newValue = {
          ...initialSettings.auditconfig,
          ...value.reduce((acc, { name, value }) => ({ ...acc, [name]: Number(value) }), {})
        };
      } else {
        newValue = {
          ...initialSettings.auditconfig,
          [property]: newValue
        };
      }

      initialSettings.auditconfig = newValue;
    }

    if (
      (parent === 'defaultnotificationsettings' ||
        parent === 'defaultusersettings' ||
        parent === 'mysigui' ||
        parent === 'outlookaddin' ||
        parent === 'gmailaddin' ||
        parent === 'personas' ||
        parent === 'billing' ||
        parent === 'csvtree' ||
        parent === 'custompurge' ||
        parent === 'rolebindings' ||
        parent === 'branding' ||
        parent === 'defaultsyncoptions') &&
      property !== null
    ) {
      captureOriginal(parent);

      if (property === 'defaultSignatureMode' || property === 'fontweight' || property === 'headerfontweight') {
        newValue = value.value;
      }

      if (parent === 'personas') {
        newValue = newValue.filter((item) => item.checked && item.value).map((item) => item.value);
      }

      newValue = {
        ...initialSettings[parent],
        [property]: newValue
      };

      initialSettings[parent] = newValue;
    }

    if (id === 'compliancetracking3p') {
      parent = null;

      value.forEach((item) => {
        captureOriginal(item.value);
        update[item.value] = item.checked;
      });
    }

    if (id === 'disclaimers') {
      parent = null;

      Object.keys(newValue).forEach((key) => {
        captureOriginal(key);
        update[key] = newValue[key];
      });
    }

    if (id === 'cname') {
      captureOriginal('cnames');
      update.cnames = [...(updatedSettings.cnames ?? settings.cnames), newValue].filter(
        (value, index, self) => self.indexOf(value) === index
      );
      parent = null;
    }

    if (parent) {
      captureOriginal(parent);
      update[parent] = newValue;
    }

    // Merge updates into updatedSettings instead of immediately applying
    updatedSettings = { ...updatedSettings, ...update };

    // Track the setting metadata for display
    const settingKey = formattedId || id;
    const existingIndex = updatedSettingsList.findIndex((s) => s.key === settingKey);

    const settingInfo = {
      key: settingKey,
      label: label,
      category: selectedCategory ? $format(selectedCategory.label) : '',
      subsection: selectedCategory ? findSubsection(id, selectedCategory) : ''
    };

    if (existingIndex >= 0) {
      updatedSettingsList[existingIndex] = settingInfo;
    } else {
      updatedSettingsList = [...updatedSettingsList, settingInfo];
    }

    // Update the step data - only store what's needed for execution, rollback, and review
    step.data = {
      id: 'domain-settings',
      rollbackData: {
        originalValues: originalValues,
        updates: updatedSettings,
        updatedSettingsList: updatedSettingsList
      }
    };

    notify(`${label} will be updated when workflow executes`, 'info');

    // Check if we have any updates to enable next button
    nextDisabled = Object.keys(updatedSettings).length === 0;
  };

  const initialize = async () => {
    loading = true;

    settings = await DomainService.getSettings();

    categories = deepCopyArray(DOMAIN_CATEGORIES)
      .map((config) => {
        if (config.items) {
          config.items = config.items
            .map((item) => {
              return (
                (!item.permission ||
                  (item.permission && hasCapability('read', item.permission)) ||
                  (item.permission === 'microsoft' && (settings?.office365enabled || $userStore.isStaff)) ||
                  (item.permission === 'google' && (settings?.googleenabled || $userStore.isStaff))) &&
                item
              );
            })
            .filter(Boolean);

          return (
            config.items.length &&
            (!config.permission || (config.permission && hasCapability('read', config.permission))) &&
            config
          );
        }

        return (!config.permission || (config.permission && hasCapability('read', config.permission))) && config;
      })
      .filter(Boolean);

    initialSettings = merge({}, settings);

    // Restore from step data if exists
    if (step.data.rollbackData?.updates) {
      updatedSettings = step.data.rollbackData.updates;
      nextDisabled = Object.keys(updatedSettings).length === 0;
    }

    if (step.data.rollbackData?.originalValues) {
      originalValues = step.data.rollbackData.originalValues;
    }

    if (step.data.rollbackData?.updatedSettingsList) {
      updatedSettingsList = step.data.rollbackData.updatedSettingsList;
    }

    // Always start with the first category
    selectedCategory = categories[0];
    if (selectedCategory) {
      selectedCategory.label = $format(selectedCategory.label);
    }

    loading = false;
  };

  const handleCategoryChange = (event) => {
    const option = event.detail;
    if (option) {
      const category = categories.find((cat) => cat.id === option.categoryId);
      selectedCategory = category;

      // Format the selectedCategory label
      if (selectedCategory) {
        selectedCategory.label = $format(selectedCategory.label);
      }
    }
  };

  // Execute function to apply all updates
  const execute = async () => {
    try {
      const response = await DomainService.updateSettings(updatedSettings);

      if (!response.status) {
        notify($format('label.SUCCESS.UPDATE_DOMAIN_SETTINGS'), 'success');
        return { success: true };
      } else {
        notify($format('label.FAILURE.UPDATE_DOMAIN_SETTINGS'), 'error');
        return { success: false, error: response };
      }
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_DOMAIN_SETTINGS'), 'error');
      return { success: false, error };
    }
  };

  // Rollback function - restore original values
  const rollback = async () => {
    try {
      const rollbackData = step.data.rollbackData?.originalValues;

      if (!rollbackData || Object.keys(rollbackData).length === 0) {
        return { success: true };
      }

      const response = await DomainService.updateSettings(rollbackData);

      if (!response.status) {
        notify($format('label.SUCCESS.ROLLBACK_DOMAIN_SETTINGS'), 'success');
        return { success: true };
      } else {
        notify($format('label.FAILURE.ROLLBACK_DOMAIN_SETTINGS'), 'error');
        return { success: false, error: response };
      }
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_DOMAIN_SETTINGS'), 'error');
      return { success: false, error };
    }
  };

  onMount(async () => {
    await initialize();

    // Register execute and rollback functions
    if (onExecute) {
      onExecute(execute);
    }
    if (onRollback) {
      onRollback(rollback);
    }
  });

  $: selectOptions = categories.length ? buildSelectOptions(categories) : [];
  $: SectionComponent = selectedCategory ? sectionComponents[selectedCategory.id] : null;
  $: updatedCount = Object.keys(updatedSettings).length;
</script>

<div class="domain-settings-update-form">
  {#if loading}
    <div class="spinner-container">
      <Spinner show inline />
    </div>
  {:else}
    <Section>
      <SectionItem
        label="Domain Setting Section"
        description="Select the domain setting section you want to update"
        htmlFor="category-select"
      >
        <Select
          id="category-select"
          items={selectOptions}
          value={selectedCategory}
          on:change={handleCategoryChange}
          placeholder="Choose a setting section..."
          clearable={false}
          showChevron
          searchable
          itemFilter={categoryItemFilter}
        >
          <div slot="empty" class="empty">
            {$format('label.NO_OPTIONS_AVAILABLE')}
          </div>
        </Select>
      </SectionItem>

      {#if updatedCount > 0}
        <SectionItem>
          <div class="updated-settings-list">
            <div class="list-header">
              <Icon name="info" />
              <strong>{updatedCount}</strong>
              {updatedCount === 1 ? 'setting' : 'settings'} will be updated:
            </div>
            <ul class="settings-list">
              {#each updatedSettingsList as setting}
                <li>
                  <span class="setting-path">
                    {setting.category}
                    {#if setting.subsection}
                      <Icon name="chevron-right" />
                      {setting.subsection}
                    {/if}
                    <Icon name="chevron-right" />
                    <strong>{setting.label}</strong>
                  </span>
                </li>
              {/each}
            </ul>
          </div>
        </SectionItem>
      {/if}
    </Section>

    {#if SectionComponent && selectedCategory}
      <div class="section-content">
        <svelte:component this={SectionComponent} bind:settings {onUpdate} {categories} />
      </div>
    {/if}
  {/if}
</div>

<style lang="scss">
  .domain-settings-update-form {
    padding: 1rem;
  }

  .spinner-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }

  .empty {
    color: var(--color-gray-600);
    padding: var(--padding-xlarge);
    text-align: center;
  }

  .updated-settings-list {
    padding: 0.75rem 1rem;
    background-color: #e8f4f8;
    border: 1px solid #b8dce8;
    border-radius: 4px;
    color: #0c5460;

    .list-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.75rem;

      :global(i) {
        font-size: 1.2rem;
      }

      strong {
        color: #004085;
      }
    }

    .settings-list {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        padding: 0.5rem 0;
        border-top: 1px solid #b8dce8;

        &:first-child {
          padding-top: 0.75rem;
        }

        &:last-child {
          padding-bottom: 0;
        }
      }
    }

    .setting-path {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.9rem;

      :global(i) {
        font-size: 0.75rem;
        opacity: 0.6;
      }

      strong {
        color: #004085;
        font-weight: 600;
      }
    }
  }

  .section-content {
    margin-top: 1.5rem;

    :global(> * > *:not(:first-child)) {
      margin-top: 1.5rem;
    }
  }
</style>
